import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import CustomDate from "../../CustomForm/CustomDate";
import CustomTextArea from "../../CustomForm/CustomTextArea";
import FileUpload from "../../CustomForm/FileUpload";
import { useMutation, useQuery } from "react-query";
import { QK } from "../../../base/qk";
import { getDolsById, updateDols } from "../../../api/services/document";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import CloseIcon from "@mui/icons-material/Close";

const EditDolsModal = ({ show, handleClose, selectedDols, refetchDols }) => {
  const { id: clientId } = useParams();

  const [formData, setFormData] = useState({
    application_date: "",
    issue_date: "",
    renewal_date: "",
    note_message: "",
    dols_cert: null,
    fileName: "No file chosen",
    reported_to_CHC: false,
  });

  const [changedFields, setChangedFields] = useState({}); // <-- track changes here
  const [isChecked, setIsChecked] = useState(false);
  const [initialChecked, setInitialChecked] = useState(false);

  const { data: dols } = useQuery(
    [QK.dols, selectedDols],
    () => getDolsById(selectedDols),
    { enabled: !!selectedDols }
  );

  useEffect(() => {
    if (dols) {
      const initialStatus = dols.status === "CURRENT";
      setFormData({
        application_date: dols.application_date || "",
        issue_date: dols.issue_date || "",
        renewal_date: dols.renewal_date || "",
        note_message: dols.note_message || "",
        dols_cert: dols.dols_cert,
        reported_to_CHC: dols.reported_to_CHC,
        fileName: dols.dols_cert ? dols.dols_cert.split("/").pop() : "No file chosen",
      });
      setIsChecked(initialStatus);
      setInitialChecked(initialStatus);
      setChangedFields({});
    }
  }, [dols]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setChangedFields(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        dols_cert: file,
        fileName: file.name,
      }));
      setChangedFields(prev => ({
        ...prev,
        dols_cert: file,
      }));
    }
  };

  const handleToggleChange = () => {
    const newStatus = !isChecked;
    setIsChecked(newStatus);
    setChangedFields(prev => ({
      ...prev,
      status: newStatus ? "CURRENT" : "SUPERSEDED",
    }));
  };

  const handleReportedToChcChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      reported_to_CHC: checked,
    }));
    setChangedFields(prev => ({
      ...prev,
      reported_to_CHC: checked,
    }));
  };

  const editMutation = useMutation(
    ({ id, editData }) => updateDols(id, editData),
    {
      onSuccess: () => {
        enqueueSnackbar("DoLS has been updated!", { variant: "success" });
        refetchDols();
        handleClose();
      },
      onError: (error) => {
        console.error("Error updating DoLS:", error.message);
      },
    }
  );

  const handleDolsSubmit = (e) => {
    e.preventDefault();
    const dolsData = new FormData();

    Object.entries(changedFields).forEach(([key, value]) => {
      dolsData.append(key, value);
    });
    dolsData.append("client", clientId);
    dolsData.append("id", selectedDols);

    editMutation.mutate({ id: selectedDols, editData: dolsData });
  };

  return (
    <Dialog open={show} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" color="primary">
          Edit Deprivation of Liberty Safeguard (DoLS)
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleDolsSubmit}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <CustomDate
                label="DoLS Application Date"
                name="application_date"
                value={formData.application_date}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomDate
                label="DoLS Issue Date"
                name="issue_date"
                value={formData.issue_date}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomDate
                label="DoLS Renewal Date"
                name="renewal_date"
                value={formData.renewal_date}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={isChecked} onChange={handleToggleChange} />}
                label="Status (CURRENT)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.reported_to_CHC}
                    onChange={handleReportedToChcChange}
                    name="reported_to_CHC"
                    color="primary"
                  />
                }
                label={
                  <Tooltip title="Reported to CHC">
                    <span>
                      Reported to CHC <FontAwesomeIcon icon={faCircleInfo} />
                    </span>
                  </Tooltip>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextArea
                label="Note"
                name="note_message"
                value={formData.note_message}
                onChange={handleChange}
              />
            </Grid>

            {dols?.dols_cert && (
              <Grid item xs={12}>
                <Typography variant="body2" className="mb-2">
                  <a href={dols.dols_cert} target="_blank" rel="noopener noreferrer">
                    View Current DoLS Cert
                  </a>{" "}
                  or select another to replace
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <FileUpload
                label="Upload DoLS Cert"
                name="dols_cert"
                onChange={handleFileChange}
                helperText="Only PDF or image files allowed"
                error={false}
                defaultFileName={formData.fileName}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
          <Button type="submit" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditDolsModal;